import { FormEvent, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const redirectTarget = useMemo(() => {
    const state = location.state as { from?: string } | null;
    return state?.from && state.from.startsWith("/") ? state.from : "/";
  }, [location.state]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await login(email, password);
      navigate(redirectTarget, { replace: true });
    } catch (err: any) {
      setError(err?.message || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto flex min-h-screen max-w-6xl items-center px-6 py-12">
        <div className="grid w-full gap-10 lg:grid-cols-[1.2fr_0.8fr]">
          <section className="hidden rounded-[2rem] border border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(168,85,247,0.22),_transparent_42%),linear-gradient(180deg,rgba(15,23,42,0.96),rgba(2,6,23,0.96))] p-10 shadow-2xl lg:block">
            <div className="mb-8 flex items-center gap-4">
              <img src="/migra-logo-48.png" alt="MigraMarket logo" className="h-14 w-14 rounded-2xl" />
              <div>
                <p className="text-3xl font-semibold tracking-tight">MigraMarket</p>
                <p className="text-sm text-slate-300">Standalone marketing operations platform</p>
              </div>
            </div>

            <div className="space-y-5">
              <h1 className="max-w-2xl text-5xl font-semibold tracking-tight text-white">
                Run content, campaigns, and publishing from one standalone desk.
              </h1>
              <p className="max-w-xl text-base leading-7 text-slate-300">
                Manage daily marketing execution across social, content, SEO, email, analytics, and
                autopilot workflows without bouncing through another product shell.
              </p>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-xs uppercase tracking-[0.24em] text-violet-300">Publishing</p>
                <p className="mt-2 text-xl font-semibold">Social, video, and content workflows</p>
                <p className="mt-2 text-sm text-slate-300">Facebook, Instagram, LinkedIn, X, TikTok, YouTube, and more.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-xs uppercase tracking-[0.24em] text-cyan-300">Revenue</p>
                <p className="mt-2 text-xl font-semibold">Lead capture tied to offers</p>
                <p className="mt-2 text-sm text-slate-300">Drive traffic into your services pipeline and direct-response campaigns.</p>
              </div>
            </div>
          </section>

          <section className="mx-auto w-full max-w-md rounded-[2rem] border border-slate-200 bg-white p-8 shadow-xl">
            <div className="mb-8 flex items-center gap-3">
              <img src="/migra-logo-48.png" alt="MigraMarket logo" className="h-12 w-12 rounded-2xl" />
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Welcome back</h1>
                <p className="text-sm text-slate-500">Sign in to your standalone MigraMarket workspace</p>
              </div>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {error ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              ) : null}

              <div>
                <label className="label" htmlFor="email">Email</label>
                <input
                  id="email"
                  className="input"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label className="label" htmlFor="password">Password</label>
                <input
                  id="password"
                  className="input"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              <button className="btn-primary w-full justify-center py-3 text-base" disabled={submitting} type="submit">
                {submitting ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <p className="mt-6 text-center text-xs text-slate-500">
              This workspace is the MigraMarketing product surface at marketing.migrahosting.com.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
